import {
  useMemo,
  type Ref,
  type RefCallback,
  type MutableRefObject,
} from "react";

/**
 * Custom hook for merging multiple React refs into a single callback ref.
 * @see https://floating-ui.com/docs/react-utils#usemergerefs
 *
 * Features:
 * - Merges an array of refs into a single callback ref
 * - Handles both function refs and object refs
 * - Optimized with useMemo for performance
 * - Returns null when all refs are null/undefined
 * - Supports generic types for type safety
 * - Automatically handles ref assignment and cleanup
 *
 * @param refs - Array of React refs to merge
 * @returns Single callback ref that handles all provided refs, or null if all refs are null
 */
export function useMergeRefs<Instance>(
  refs: Array<Ref<Instance> | undefined>,
): RefCallback<Instance> | null {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => mergeRefs(refs), refs);
}

/**
 * Merges an array of refs into a single callback ref.
 *
 * Features:
 * - Merges an array of refs into a single callback ref
 * - Handles both function refs and object refs
 *
 * @param refs - Array of React refs to merge
 * @returns
 */
export function mergeRefs<Instance>(
  refs: Array<Ref<Instance> | undefined>,
): RefCallback<Instance> | null {
  if (refs.every((ref) => ref == null)) {
    return null;
  }

  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as MutableRefObject<Instance | null>).current = value;
      }
    });
  };
}
